/**
 * Global Page View that helps handle generic functionality
 * across the app, such as printing, popups, and analytics.
 *
 * @TODO make this a react component
 */

import * as fpti from './analytics/fpti'
import $ from 'jquery'
import iframeSupport, { postMessageUp } from 'iframe-support'
import serverProps from '../transfer/utils/serverProps'
import serverPropsXB from '../calculator/lib/server-props'
import * as pxp from './pxp'
import logger from 'loglevel'
import neff from 'neff'
import { setupHeader } from './header' // view handler for the header
import { fetch } from '../calculator/lib/xhr'
import { ActionTypes as types } from '../transfer/constants'
import { ActionTypes as xbTypes } from '../calculator/constants'
import { startClientErrorTracking } from './errors'
import { startContentErrorTracking } from './errors/content'

// TODO: handle server props consistently across apps...
const data = serverPropsXB.token ? serverPropsXB : serverProps

export function setupGlobalEventHandlers() {
  setupPXP()
  setupCSRF()
  setupAJAXErrors()
  setupHeader()
  setupAnalytics()
  setupIFrame()
  setupEvents()
  setupLogging()
  setupErrorTracking()
  setupServiceWorker()
}

function setupPXP() {
  pxp.addTreatments(data.experiments)
}

function setupCSRF() {
  $.ajaxPrefilter(function(opts, origOpts, jqXHR) {
    const csrf = data.token
    jqXHR.setRequestHeader('X-CSRF-Token', csrf)
  })
}

function setupAJAXErrors() {
  // low-level jquery error handler
  // TODO: can i just put this in lib/xhr.js or something?
  $(document).ajaxError(handleAjaxError)
}

function setupLogging() {
  // setup log level for the environment
  if (neff.isEnabled('verbose-logging')) {
    logger.enableAll()
  } else {
    logger.disableAll()
  }
}

function setupErrorTracking() {
  // start capturing global and content errors first thing
  startClientErrorTracking('/myaccount/transfer/error')
  startContentErrorTracking('/myaccount/transfer/missingContent')
}

// TODO: this is never needed in XB, we could
// potentially just move this to transfer
function setupIFrame() {
  const { externalConfig = {} } = data
  const resizer = !externalConfig.modernIframeSupport
  const stylizer = resizer
  iframeSupport({ resizer, stylizer })
}

function setupEvents() {
  $(document.body).on('click', '.popup', e => popup(e))
  $(document.body).on('click', '.print-btn', e => initiatePrint(e))
}

/**
 * All of the analytics listeners are setup right here. This is great
 * because it means our analytics library doesn't know anything about
 * our app.
 */
function setupAnalytics() {
  $(window).on('popstate', fpti.trackBackButton)
  $(document.body).on('click', '[data-pagename]', event =>
    fpti.trackElement($(event.currentTarget))
  )
}

function setupServiceWorker() {
  const fptiObj = window.fpti || {}
  fptiObj.message = ''

  // check if browser supports service worker
  if ('serviceWorker' in navigator) {
    const p2pScope = '/myaccount/transfer/'
    const swModes = {
      passThru: 0,
      cacheThenUpdate: 1,
    }

    if (!pxp.isTreatment('Trmt_p2p_service_worker')) {
      fptiObj.message = 'sw_control'
      if (navigator.serviceWorker.controller) {
        fptiObj.message = 'sw_control_passThru'
        navigator.serviceWorker.controller.postMessage(swModes.passThru)
      }
    } else {
      window.addEventListener('load', () => {
        // Register only if no active worker or a force refresh (shift + refresh)
        if (!navigator.serviceWorker.controller) {
          navigator.serviceWorker
            .register('/myaccount/transfer/sw.js', {
              scope: p2pScope,
            })
            .then(registration => {
              fptiObj.message = 'sw_trmt_registered'
              navigator.serviceWorker.controller.postMessage(
                swModes.cacheThenUpdate
              )
            })
            .catch(registrationError => {
              fptiObj.message = 'sw_trmt_register_failed'
              logger.info(
                '>>>SWPOC - Registering service worker failed',
                JSON.stringify(registrationError)
              )
            })
        }
        fptiObj.message = 'sw_trmt_repeated'
        if (navigator.serviceWorker.controller) {
          navigator.serviceWorker.controller.postMessage(
            swModes.cacheThenUpdate
          )
        }
      })
    }
  } else {
    fptiObj.message = 'sw_not_supported'
    logger.info('>>>SWPOC - Service Worker >NOT< Supported')
  }
}

/**
 * Handle AJAX Error
 * @TODO can we handle this in xhr.js or something?
 */
function handleAjaxError(event, jqxhr, settings, exception) {
  // allow for users to by-pass the default error handling
  if (settings.ignoreErrors) {
    return
  }

  // try to grab the error object from the response
  let error
  try {
    error = JSON.parse(jqxhr.responseText)
  } catch (e) {
    logger.warn(e)
    error = jqxhr.responseText
  }

  postMessageUp({
    statusMessage: 'asyncErrorMessage',
    error: error && error.error,
  })

  // handle 401 and 302 re-directs
  if (jqxhr.status === 401 || jqxhr.status === 302) {
    // extract the location header (or in some cases the location may be
    // explicitly returned as part of hte JSON response?)
    const newPath =
      (error && error.location) || jqxhr.getResponseHeader('location')

    // if a new path was successfully retrieved from the responseText, do a redirect
    // otherwise, we'll allow the local error handlers to take over
    if (newPath) {
      window.location.href = newPath
      return false
    }
  }
}

/**
 * Opens window in a new popup.
 *
 * @param event
 */
function popup(event) {
  event.preventDefault()
  const currentTarget = event.currentTarget,
    href = currentTarget.getAttribute('href'),
    width = currentTarget.getAttribute('data-width') || '640',
    height = currentTarget.getAttribute('data-height') || '400',
    windowOptions = `width=${width}, height=${height}`

  window.open(href, 'popup', windowOptions)

  return false
}

/**
 * Invoke the standard OS print dialog
 *
 * @param event
 */
function initiatePrint(event) {
  event.preventDefault()
  window.print()
}

export function loadGriffinMetadata() {
  const store = require('../transfer/stores/configureStore').default
  const xbStore = require('../calculator/stores/configure-store').default

  const {
    address: { addresses = [] } = {},
    server: { griffinMetadata = {} } = {},
  } = store.getState()
  const countries = addresses
    .map(addr => addr.countryCode || addr.country_code)
    .filter((code, index, list) => list.indexOf(code) === index)

  // if server already contains griffinMetadata, like Guest flow, we skip load metadata here.
  if (Object.keys(griffinMetadata).length !== 0) {
    return Promise.resolve()
  }

  // fetch griffin meta and update store
  const url = `/myaccount/transfer/griffinMetadata`
  return fetch(url, { countries })
    .then(response => {
      store.dispatch({
        type: types.USER.RECEIVED_GRIFFIN_METADATA,
        data: response,
      })
      xbStore.dispatch({
        type: xbTypes.XB_RECEIVED_GRIFFIN_METADATA,
        data: response.griffinMetadata,
      })
    })
    .catch(err => logger.error(err))
}

export function applyPolyfill(arr) {
  arr.forEach(function(item) {
    if (item.hasOwnProperty('append')) {
      return
    }
    Object.defineProperty(item, 'append', {
      configurable: true,
      enumerable: true,
      writable: true,
      value: function append() {
        var argArr = Array.prototype.slice.call(arguments),
          docFrag = document.createDocumentFragment()

        argArr.forEach(function(argItem) {
          var isNode = argItem instanceof Node
          docFrag.appendChild(
            isNode ? argItem : document.createTextNode(String(argItem))
          )
        })

        this.appendChild(docFrag)
      },
    })
  })
}
